import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxDropDownButtonModule } from 'devextreme-angular/ui/drop-down-button';
import { DxToolbarModule } from 'devextreme-angular/ui/toolbar';
import { AuthService } from 'src/app/auth/auth.service';
import { RuntimeConfigService } from 'src/app/core/services/runtime-config.service';
import { UserPanelComponent } from 'src/app/shared/components/user-panel/user-panel.component';
import { EnvironmentName } from 'src/environments/environment.model';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { ThemeSwitcherComponent } from '../theme-switcher/theme-switcher.component';

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [
        CommonModule,
        DxButtonModule,
        DxDropDownButtonModule,
        DxToolbarModule,
        RouterLink,
        RouterLinkActive,
        ThemeSwitcherComponent,
        LanguageSwitcherComponent,
        UserPanelComponent,
        TranslocoPipe,
    ],
    templateUrl: './app-header.component.html',
    styleUrl: './app-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeaderComponent {
    @Input() menuToggleEnabled = false;

    @Input()
    title!: string;

    @Output() menuToggle = new EventEmitter<void>();

    private readonly runtimeConfigService = inject(RuntimeConfigService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly translocoService = inject(TranslocoService);

    userMenuItems = [
        {
            text: this.translocoService.translate('profile'),
            icon: 'cn-icon-ico-user',
            onClick: () => {
                this.router.navigate(['/profile']);
            },
        },
        {
            text: this.translocoService.translate('logout'),
            icon: 'dx-icon-runner dx-list-item-icon',
            onClick: () => {
                this.authService.logout();
            },
        },
    ];

    // Administration / Development dropdown items
    readonly administrationMenuItems = [
        { text: this.translocoService.translate('users_list'), icon: 'cn-icon-customers', route: '/user-list' },
        { text: this.translocoService.translate('roles'), icon: 'cn-icon-home', route: '/roles' },
        { text: this.translocoService.translate('shipping_methods'), icon: 'dx-icon-airplane', route: '/shipping-methods' },
        { text: this.translocoService.translate('payment_conditions'), icon: 'dx-icon-money', route: '/payment-conditions' },
        { text: this.translocoService.translate('raw_materials'), icon: 'dx-icon-box', route: '/raw-materials' },
    ];

    readonly ordersMenuItems = [
        { text: this.translocoService.translate('customer_orders'), icon: 'cn-icon-ico-orders', route: '/orders' },
        { text: this.translocoService.translate('deliveries'), icon: 'dx-icon-exportselected', route: '/deliveries' },
        { text: this.translocoService.translate('invoices'), icon: 'dx-icon-money', route: '/invoices' },
    ];

    readonly developmentMenuItems = [
        { text: 'Geo Format', icon: 'dx-icon-file', route: '/geo' },
        { text: 'Stl Format', icon: 'dx-icon-file', route: '/stl' },
        { text: 'Icons', icon: 'dx-icon-smalliconslayout', route: '/icons' },
    ];

    readonly appLauncherMenuItems = [
        { text: this.translocoService.translate('smart_factory_suite'), icon: 'cn-icon-ico-smart-factory', href: 'https://www.google.com' },
        { text: this.translocoService.translate('trumpf_shop'), icon: 'cn-icon-ico-mytrumpf-shop', href: 'https://www.google.com' },
        { text: this.translocoService.translate('service_app'), icon: 'cn-icon-ico-service-app', href: 'https://www.google.com' },
    ];

    get administrationButtonText(): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(`<i class="icon-articles"></i> Administration`);
    }

    get isDevelopmentEnvironment(): boolean {
        const envName = this.runtimeConfigService.getEnvironment();
        console.log(envName);
        return envName !== EnvironmentName.PROD && envName !== EnvironmentName.STAGE;
    }

    onMenuToggle(): void {
        this.menuToggle.emit();
    }
}
